import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ScopeTranscript } from "@keydials/api-client";
import { useWorkspaceServices, type InstanceRealtimeHandlers } from "../context";
import { article, DEFAULT_SEGMENT_NOUN, type SegmentNoun } from "../vocabulary";
import { TranscriptMessageRow, toExportMessage } from "./ConversationThread";
import { TranscriptExportButtons } from "./TranscriptExportButtons";
import { groupTranscript, transcriptSummary, type TranscriptStructureSection } from "./scopeTranscript";

/** Without an instance-wide realtime hook the transcript still loads whenever its tab opens; it just doesn't
 *  refresh while open. Module-level, so the hook call below is unconditional and stable. */
function noInstanceRealtime(_instanceId: string, _handlers: InstanceRealtimeHandlers): void {}

/** A burst of posts — a quick back-and-forth — costs one refetch of the whole scope, not one per message. */
export const TRANSCRIPT_REFRESH_DEBOUNCE_MS = 400;

/**
 * The section and whole-dial Discussion tab (KEY-559): everything said in the action points (and on their
 * items) this reader can see, grouped by where it was said. On a Group Key Dial's whole-dial tab that includes
 * its child dials (KEY-567), each child's groups named with the dial they were said on.
 *
 * READ-ONLY by decision. A conversation always belongs to one action point — the server refuses a post
 * without one — so there is nothing to post "to a section". Each group links to its action point instead,
 * which is where the thread is answered.
 *
 * Every filtering decision is the server's: which action points this reader can see, which restricted
 * messages they were addressed on — per child dial, too. This component only arranges what it is given.
 */
export function ScopeDiscussionTranscript({
  instanceId,
  sectionKey = null,
  sections,
  meUserId,
  onOpenAp,
  onOpenChildAp,
  noun = DEFAULT_SEGMENT_NOUN,
  isGroup = false,
  exportTitle,
  exportSubtitle,
}: {
  instanceId: string;
  /** The section to read; omitted for the whole dial. */
  sectionKey?: string | null;
  /** The dial's structure, used only to order and name the groups — on a section page, just that section. */
  sections: readonly TranscriptStructureSection[];
  meUserId: string | null;
  /** Opens the action point a group was said on. */
  onOpenAp: (sectionKey: string, segmentKey: string) => void;
  /** Opens an action point inside a child dial (KEY-567). Absent where the host can't drill into a child, in
   *  which case a child's group has no button. */
  onOpenChildAp?: (childInstanceId: string, sectionKey: string, segmentKey: string) => void;
  /** This dial's word for an action point (KEY-539). */
  noun?: SegmentNoun;
  /** A Group Key Dial, whose whole-dial transcript also holds its children's discussion (KEY-567). */
  isGroup?: boolean;
  /** KEY-591: what this transcript is, for its download / print — the dial's name, or "Dial › Section". Without it
   *  the tab offers no export. */
  exportTitle?: string;
  /** Lines under the export's title: the organisation, and so on. */
  exportSubtitle?: (string | null | undefined)[];
}) {
  const services = useWorkspaceServices();
  const { api, getAccessToken } = services;
  const useInstanceRealtime = services.useInstanceRealtime ?? noInstanceRealtime;
  const [transcript, setTranscript] = useState<ScopeTranscript | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const token = getAccessToken();
    if (!token) {
      return;
    }
    try {
      setTranscript(await api.listScopeTranscript(token, instanceId, sectionKey));
      setError(null);
    } catch {
      setError("Could not load the discussion.");
    }
  }, [instanceId, sectionKey]);

  // A different scope is a different transcript: clear the old one rather than show it under the new heading.
  useEffect(() => {
    setTranscript(null);
    setError(null);
    void load();
  }, [load]);

  const pending = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (pending.current) {
        clearTimeout(pending.current);
      }
    },
    [],
  );
  useInstanceRealtime(instanceId, {
    onEvent: (event) => {
      // The instance group carries every kind of change on the dial; only a message posted or deleted alters
      // a transcript (a deleted one stays in it as a tombstone, KEY-582). The refetch is re-authorised and
      // re-filtered server-side, so an event about a thread this reader can't see just comes back unchanged.
      // A post inside a child dial is published on the CHILD, so on a group it shows the next time the tab
      // loads rather than live.
      if (event?.type !== "message-posted" && event?.type !== "message-deleted") {
        return;
      }
      if (pending.current) {
        clearTimeout(pending.current);
      }
      pending.current = setTimeout(() => {
        pending.current = null;
        void load();
      }, TRANSCRIPT_REFRESH_DEBOUNCE_MS);
    },
    onReconnect: load,
  });

  const groups = useMemo(
    () => (transcript ? groupTranscript(transcript.messages, sections, instanceId, transcript.childDials ?? []) : []),
    [transcript, sections, instanceId],
  );
  const where = sectionKey ? "this section" : "this dial";

  return (
    <div className="kd-card" style={{ padding: "var(--pad-card)", display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: "var(--text-muted)" }}>
          Everything said in {sectionKey ? "this section’s" : "this dial’s"} {noun.many} and their items that you can
          see. To reply, open the {noun.one}.
        </p>
        {isGroup && !sectionKey && (
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "var(--text-faint)" }}>
            That includes this group’s subdials — each is named above what was said in it.
          </p>
        )}
      </div>

      {error ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--danger-text, #c11118)" }}>{error}</p>
      ) : transcript === null ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--text-faint)" }}>Loading discussion…</p>
      ) : groups.length === 0 ? (
        <p style={{ margin: 0, fontSize: 14, color: "var(--text-faint)" }}>Nothing has been said in {where} yet.</p>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div style={{ fontSize: 11.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--text-faint)" }}>
              {transcriptSummary(groups, noun)}
            </div>
            {exportTitle && (
              <TranscriptExportButtons
                build={() => ({
                  title: exportTitle,
                  subtitle: exportSubtitle,
                  note: transcript.truncated
                    ? `This transcript holds the latest ${transcript.messages.length} messages. Earlier messages are on each ${noun.one}'s own thread.`
                    : null,
                  groups: groups.map((group) => ({
                    dialName: group.dialName,
                    heading: group.label,
                    messages: group.rows.map((row) => toExportMessage(row.message, meUserId, row.itemName)),
                  })),
                })}
              />
            )}
          </div>
          {transcript.truncated && (
            // The server's cap. Each thread is still whole on its own action point, so say where to find it.
            <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)" }}>
              Showing the latest {transcript.messages.length} — open {article(noun.one)} {noun.one} for its full thread.
            </p>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {groups.map((group) => {
              const open =
                group.instanceId === instanceId
                  ? () => onOpenAp(group.sectionKey, group.segmentKey)
                  : onOpenChildAp
                    ? () => onOpenChildAp(group.instanceId, group.sectionKey, group.segmentKey)
                    : null;
              return (
                <section
                  key={`${group.instanceId}::${group.sectionKey}::${group.segmentKey}`}
                  aria-label={group.dialName ? `${group.dialName} › ${group.label}` : group.label}
                >
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
                    <div style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                      {group.dialName && (
                        <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-faint)", overflowWrap: "anywhere" }}>
                          {group.dialName}
                        </div>
                      )}
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text)", overflowWrap: "anywhere" }}>{group.label}</div>
                    </div>
                    {open && (
                      <button type="button" onClick={open} style={{ ...linkBtn, flex: "none" }}>
                        Open {noun.one}
                      </button>
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, borderLeft: "2px solid var(--border)", paddingLeft: 12 }}>
                    {group.rows.map((row) => (
                      <TranscriptMessageRow key={row.message.id} message={row.message} meUserId={meUserId} context={row.itemName} />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

const linkBtn = {
  font: "inherit",
  fontSize: 12.5,
  fontWeight: 600,
  padding: 0,
  border: "none",
  background: "none",
  color: "var(--text-muted)",
  textDecoration: "underline",
  cursor: "pointer",
} as const;
